import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../helpers/axiosconfig";

const normalizeActivityError = (err, fallback = "Failed to fetch activity log") => ({
  status: Number(err?.response?.status) || null,
  message: err?.response?.data?.message || err?.response?.data?.error || err?.message || fallback,
});

const isValidActivityPayload = (payload) => (
  payload?.success !== false &&
  Array.isArray(payload?.logs) &&
  payload?.pagination &&
  typeof payload.pagination === "object" &&
  !Array.isArray(payload.pagination)
);

export const fetchMyActivityLog = createAsyncThunk(
  "activityLog/fetchMy",
  async ({ page = 1, limit = 50, category, eventType, dateFrom, dateTo, channel, source } = {}, { rejectWithValue }) => {
    try {
      const params = { page, limit };
      if (category) params.category = category;
      if (eventType) params.eventType = eventType;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (channel) params.channel = channel;
      if (source) params.source = source;
      const res = await axios.get("activity-log/my", { params });
      return res.data;
    } catch (err) {
      return rejectWithValue(normalizeActivityError(err));
    }
  }
);

export const fetchUserActivityLog = createAsyncThunk(
  "activityLog/fetchUser",
  async ({ userId, page = 1, limit = 50, category, eventType, dateFrom, dateTo, channel, source } = {}, { rejectWithValue }) => {
    try {
      const params = { page, limit };
      if (category) params.category = category;
      if (eventType) params.eventType = eventType;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (channel) params.channel = channel;
      if (source) params.source = source;
      const res = await axios.get(`activity-log/user/${userId}`, { params });
      return res.data;
    } catch (err) {
      return rejectWithValue(normalizeActivityError(err));
    }
  }
);

export const fetchGlobalActivityLog = createAsyncThunk(
  "activityLog/fetchGlobal",
  async ({ page = 1, limit = 50, category, eventType, dateFrom, dateTo, channel, source } = {}, { rejectWithValue }) => {
    try {
      const params = { page, limit };
      if (category) params.category = category;
      if (eventType) params.eventType = eventType;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (channel) params.channel = channel;
      if (source) params.source = source;
      const res = await axios.get("activity-log/global", { params });
      return res.data;
    } catch (err) {
      return rejectWithValue(normalizeActivityError(err));
    }
  }
);

const activityLogSlice = createSlice({
  name: "activityLog",
  initialState: {
    logs: [],
    pagination: { page: 1, limit: 50, total: 0, pages: 1 },
    loading: false,
    error: null,
    errorStatus: null,
    currentRequestId: null,
  },
  reducers: {
    clearLogs: (state) => {
      state.logs = [];
      state.pagination = { page: 1, limit: 50, total: 0, pages: 1 };
      state.error = null;
      state.errorStatus = null;
      state.currentRequestId = null;
    },
  },
  extraReducers: (builder) => {
    const resetPagination = (state) => {
      state.pagination = { page: 1, limit: 50, total: 0, pages: 1 };
    };
    const handlePending = (state, action) => {
      state.currentRequestId = action.meta.requestId;
      state.loading = true;
      state.error = null;
      state.errorStatus = null;
      // Own history must fail closed on account switches. Admin/global reads
      // keep their confirmed snapshot visible while a refresh is in flight.
      if (action.type === fetchMyActivityLog.pending.type) {
        state.logs = [];
        resetPagination(state);
      }
    };
    const handleFulfilled = (state, action) => {
      if (state.currentRequestId !== action.meta.requestId) return;
      if (!isValidActivityPayload(action.payload)) {
        state.loading = false;
        state.logs = [];
        resetPagination(state);
        state.error = "malformed_activity_history";
        state.errorStatus = null;
        return;
      }
      state.loading = false;
      state.logs = action.payload.logs || [];
      state.pagination = action.payload.pagination || state.pagination;
      state.errorStatus = null;
    };
    const handleMyFulfilled = (state, action) => {
      if (state.currentRequestId !== action.meta.requestId) return;
      state.loading = false;

      const logs = action.payload?.logs;
      const pagination = action.payload?.pagination;
      if (action.payload?.success === false || !Array.isArray(logs) || !pagination || typeof pagination !== 'object' || Array.isArray(pagination)) {
        state.logs = [];
        resetPagination(state);
        state.error = 'malformed_activity_history';
        state.errorStatus = null;
        return;
      }

      state.logs = logs;
      state.pagination = pagination;
    };
    const handleRejected = (state, action) => {
      if (state.currentRequestId !== action.meta.requestId) return;
      state.loading = false;
      state.error = action.payload?.message || action.payload || "Failed to fetch activity log";
      state.errorStatus = Number(action.payload?.status) || null;
      if (state.errorStatus === 401 || state.errorStatus === 403) {
        state.logs = [];
        resetPagination(state);
      }
    };

    builder
      .addCase(fetchMyActivityLog.pending, handlePending)
      .addCase(fetchMyActivityLog.fulfilled, handleMyFulfilled)
      .addCase(fetchMyActivityLog.rejected, handleRejected)
      .addCase(fetchUserActivityLog.pending, handlePending)
      .addCase(fetchUserActivityLog.fulfilled, handleFulfilled)
      .addCase(fetchUserActivityLog.rejected, handleRejected)
      .addCase(fetchGlobalActivityLog.pending, handlePending)
      .addCase(fetchGlobalActivityLog.fulfilled, handleFulfilled)
      .addCase(fetchGlobalActivityLog.rejected, handleRejected);
  },
});

export const { clearLogs } = activityLogSlice.actions;
export default activityLogSlice.reducer;
